/**
 * Состояние шапки фильтров аналитики и его запись в URL.
 *
 * Имена параметров URL совпадают с именами query контракта API (`from`, `to`, `compare`, `marketplace`,
 * `sellerIds`, ...), поэтому запрос строится из того же состояния без переименований. Списки идут через запятую.
 * Значения по умолчанию в URL не пишутся: ссылка остаётся короткой, а «последние 28 дней» всегда считаются
 * от сегодняшней даты. Остальные параметры страницы (`tab`, `groupBy`, `metric`, ...) этот модуль не трогает.
 */

import { addDays, isIsoDate, todayIso } from './format'
import type { AnalyticsFilters, CompareMode, FulfillmentCode, MarketplaceCode } from './types'

/** Параметры URL, которыми владеет шапка фильтров. */
export const FILTER_URL_KEYS = [
  'from',
  'to',
  'compare',
  'marketplace',
  'fulfillment',
  'sellerIds',
  'categoryIds',
  'productions',
  'themeNumbers',
  'designCodes',
  'designerIds',
  'articles',
  'search',
] as const

/** Длина периода по умолчанию, сутки. */
export const DEFAULT_PERIOD_DAYS = 28

const COMPARE_MODES: CompareMode[] = ['prev', 'yoy', 'none']
const MARKETPLACES: MarketplaceCode[] = ['wb', 'ozon']
const FULFILLMENTS: FulfillmentCode[] = ['FBO', 'FBS', 'UNKNOWN']

/**
 * Период по умолчанию: 28 суток, заканчивающихся вчера. Сегодняшний день источники ещё не догрузили
 * (ночная загрузка идёт в 06:30), и неполные сутки искажали бы сравнение.
 */
export function defaultPeriod(now: Date = new Date()): { from: string; to: string } {
  const to = addDays(todayIso(now), -1)
  return { from: addDays(to, -(DEFAULT_PERIOD_DAYS - 1)), to }
}

export function defaultFilters(now: Date = new Date()): AnalyticsFilters {
  const { from, to } = defaultPeriod(now)
  return {
    from,
    to,
    compare: 'prev',
    marketplace: null,
    fulfillment: null,
    sellerIds: [],
    categoryIds: [],
    productions: [],
    themeNumbers: [],
    designCodes: [],
    designerIds: [],
    articles: [],
    search: '',
  }
}

/** Список из CSV: пробелы и пустые элементы отбрасываем, повторы убираем с сохранением порядка. */
export function parseList(value: string | null | undefined): string[] {
  if (!value) return []
  const result: string[] = []
  for (const part of value.split(',')) {
    const item = part.trim()
    if (item && !result.includes(item)) result.push(item)
  }
  return result
}

/** Состояние фильтров из строки запроса. Мусор в URL заменяется значениями по умолчанию. */
export function parseFilters(params: URLSearchParams, now: Date = new Date()): AnalyticsFilters {
  const base = defaultFilters(now)

  let from = params.get('from') ?? ''
  let to = params.get('to') ?? ''
  if (!isIsoDate(from) || !isIsoDate(to) || from > to) {
    from = base.from
    to = base.to
  }

  const compare = params.get('compare') as CompareMode | null
  const marketplace = params.get('marketplace') as MarketplaceCode | null
  const fulfillment = params.get('fulfillment') as FulfillmentCode | null

  return {
    from,
    to,
    compare: compare && COMPARE_MODES.includes(compare) ? compare : base.compare,
    marketplace: marketplace && MARKETPLACES.includes(marketplace) ? marketplace : null,
    fulfillment: fulfillment && FULFILLMENTS.includes(fulfillment) ? fulfillment : null,
    sellerIds: parseList(params.get('sellerIds')),
    categoryIds: parseList(params.get('categoryIds')),
    productions: parseList(params.get('productions')),
    themeNumbers: parseList(params.get('themeNumbers')),
    designCodes: parseList(params.get('designCodes')),
    designerIds: parseList(params.get('designerIds')),
    articles: parseList(params.get('articles')),
    search: (params.get('search') ?? '').trim(),
  }
}

/**
 * Записывает состояние фильтров в `params`: сначала убирает все свои ключи, затем пишет только отличающиеся
 * от умолчаний. Чужие ключи страницы остаются как были. Возвращает тот же объект.
 */
export function writeFilters(
  params: URLSearchParams,
  filters: AnalyticsFilters,
  now: Date = new Date(),
): URLSearchParams {
  for (const key of FILTER_URL_KEYS) params.delete(key)
  const base = defaultFilters(now)

  if (filters.from !== base.from || filters.to !== base.to) {
    params.set('from', filters.from)
    params.set('to', filters.to)
  }
  if (filters.compare !== base.compare) params.set('compare', filters.compare)
  if (filters.marketplace) params.set('marketplace', filters.marketplace)
  if (filters.fulfillment) params.set('fulfillment', filters.fulfillment)
  const lists: Array<[string, string[]]> = [
    ['sellerIds', filters.sellerIds],
    ['categoryIds', filters.categoryIds],
    ['productions', filters.productions],
    ['themeNumbers', filters.themeNumbers],
    ['designCodes', filters.designCodes],
    ['designerIds', filters.designerIds],
    ['articles', filters.articles],
  ]
  for (const [key, list] of lists) if (list.length > 0) params.set(key, list.join(','))
  if (filters.search.trim()) params.set('search', filters.search.trim())
  return params
}

/**
 * Query для запросов API: те же имена, что в URL. Период и режим сравнения передаются всегда явно;
 * пустые фильтры не передаются вовсе.
 */
export function toApiParams(filters: AnalyticsFilters): Record<string, string> {
  const params = writeFilters(new URLSearchParams(), filters)
  const result: Record<string, string> = {}
  params.forEach((value, key) => {
    result[key] = value
  })
  result.from = filters.from
  result.to = filters.to
  result.compare = filters.compare
  return result
}

/** Сколько фильтров применено помимо периода и режима сравнения (для кнопки «Сбросить»). */
export function activeFilterCount(filters: AnalyticsFilters): number {
  let count = 0
  if (filters.marketplace) count++
  if (filters.fulfillment) count++
  for (const list of [
    filters.sellerIds,
    filters.categoryIds,
    filters.productions,
    filters.themeNumbers,
    filters.designCodes,
    filters.designerIds,
    filters.articles,
  ]) {
    if (list.length > 0) count++
  }
  if (filters.search) count++
  return count
}

/** Сброс всех фильтров, кроме периода и режима сравнения. */
export function clearEntityFilters(filters: AnalyticsFilters, now: Date = new Date()): AnalyticsFilters {
  return {
    ...defaultFilters(now),
    from: filters.from,
    to: filters.to,
    compare: filters.compare,
  }
}
